import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EyeIcon, EyeSlashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  CreateStorageAccessKeyResult,
  createStorageAccessKey,
  deleteStorageAccessKey,
  fetchStorageCredentials,
  fetchStorageOverview,
  listStorageAccessKeys,
  ObjectStorageOverview,
  revealStorageAccessKeyCredentials,
  revokeStorageAccessKey,
  StorageAccessKey,
  StorageAccessKeyCredentials,
  updateStorageAccessKey,
} from '../../services/storageApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  StorageAccessKeyFormModal,
  StorageAccessKeyFormValues,
} from './StorageAccessKeyFormModal';

function fromDateInputValue(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return `${trimmed}T00:00:00.000Z`;
}

export function StorageAccessKeysPanel() {
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('storage');

  const [overview, setOverview] = useState<ObjectStorageOverview | null>(null);
  const [keys, setKeys] = useState<StorageAccessKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<StorageAccessKey | null>(null);
  const [createdCredentials, setCreatedCredentials] = useState<CreateStorageAccessKeyResult | null>(
    null,
  );
  const [revealedById, setRevealedById] = useState<Record<string, StorageAccessKeyCredentials>>({});
  const [revealingId, setRevealingId] = useState<string | null>(null);

  const [connectionVisible, setConnectionVisible] = useState(false);
  const [connectionAccessKey, setConnectionAccessKey] = useState<string | null>(null);
  const [connectionSecretKey, setConnectionSecretKey] = useState<string | null>(null);
  const [revealingConnection, setRevealingConnection] = useState(false);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const [overviewResult, keysResult] = await Promise.all([
        fetchStorageOverview(),
        listStorageAccessKeys(),
      ]);
      setOverview(overviewResult);
      setKeys(keysResult);
      setConnectionVisible(false);
      setConnectionAccessKey(null);
      setConnectionSecretKey(null);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar chaves de acesso.'));
      setOverview(null);
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  function openCreateModal(): void {
    setEditing(null);
    setModalOpen(true);
  }

  function openEditModal(item: StorageAccessKey): void {
    setEditing(item);
    setModalOpen(true);
  }

  function closeModal(): void {
    if (saving) return;
    setModalOpen(false);
    setEditing(null);
  }

  async function handleSubmit(values: StorageAccessKeyFormValues): Promise<void> {
    if (!canWrite) return;

    if (!values.name) {
      toast.error('Informe o nome da chave.');
      return;
    }

    const payload = {
      name: values.name,
      description: values.description || null,
      expiresAtUtc: fromDateInputValue(values.expiresAtUtc),
    };

    setSaving(true);
    try {
      if (editing) {
        await updateStorageAccessKey(editing.id, payload);
        toast.success('Chave atualizada.');
      } else {
        const created = await createStorageAccessKey(payload);
        setCreatedCredentials(created);
        toast.success('Chave criada. Guarde o secret agora.');
      }
      setModalOpen(false);
      setEditing(null);
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar chave de acesso.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(item: StorageAccessKey): Promise<void> {
    if (!canWrite) return;
    if (!window.confirm(`Revogar a chave "${item.name}"?`)) return;

    try {
      await revokeStorageAccessKey(item.id);
      toast.success('Chave revogada.');
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao revogar chave.'));
    }
  }

  async function handleDelete(item: StorageAccessKey): Promise<void> {
    if (!canWrite) return;
    if (!window.confirm(`Excluir permanentemente a chave "${item.name}"?`)) return;

    try {
      await deleteStorageAccessKey(item.id);
      setRevealedById((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      toast.success('Chave excluída.');
      await loadData();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir chave.'));
    }
  }

  async function handleReveal(item: StorageAccessKey): Promise<void> {
    if (!canWrite) {
      toast.error('Revelar credenciais exige permissão de escrita em Armazenamento.');
      return;
    }

    if (revealedById[item.id]) {
      setRevealedById((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      return;
    }

    setRevealingId(item.id);
    try {
      const credentials = await revealStorageAccessKeyCredentials(item.id);
      setRevealedById((prev) => ({ ...prev, [item.id]: credentials }));
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao revelar credenciais da chave.'));
    } finally {
      setRevealingId(null);
    }
  }

  async function handleToggleConnectionCredentials(): Promise<void> {
    if (connectionVisible) {
      setConnectionVisible(false);
      setConnectionAccessKey(null);
      setConnectionSecretKey(null);
      return;
    }

    if (!canWrite) {
      toast.error('Revelar credenciais exige permissão de escrita em Armazenamento.');
      return;
    }

    setRevealingConnection(true);
    try {
      const credentials = await fetchStorageCredentials();
      setConnectionAccessKey(credentials.accessKey);
      setConnectionSecretKey(credentials.secretKey);
      setConnectionVisible(true);
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao revelar credenciais da conexão.'));
    } finally {
      setRevealingConnection(false);
    }
  }

  if (loading) {
    return <LoadingSpinner label="Carregando chaves de acesso..." />;
  }

  return (
    <div className="space-y-4">
      {createdCredentials ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-amber-900">
                Chave criada — copie o secret agora
              </h3>
              <p className="mt-1 text-sm text-amber-800">
                O secret completo só é exibido neste momento. Guarde em local seguro.
              </p>
            </div>
            <button
              type="button"
              className="erp-btn-secondary erp-btn-sm"
              onClick={() => setCreatedCredentials(null)}
              aria-label="Fechar aviso"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden />
            </button>
          </div>
          <dl className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-amber-800">
                Access Key
              </dt>
              <dd className="mt-1 break-all font-mono text-sm text-amber-950">
                {createdCredentials.accessKey}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-amber-800">
                Secret Key
              </dt>
              <dd className="mt-1 break-all font-mono text-sm text-amber-950">
                {createdCredentials.secretKey}
              </dd>
            </div>
          </dl>
        </div>
      ) : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Chaves adicionais</h3>
            <p className="mt-1 text-sm text-slate-500">
              No MinIO, listadas a partir do provedor; nos demais, gerenciadas pela plataforma.
              O secret fica guardado de forma segura e pode ser revelado a qualquer momento.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="erp-btn-secondary erp-btn-sm"
              onClick={() => void loadData()}
            >
              Atualizar
            </button>
            {canWrite ? (
              <button
                type="button"
                className="erp-btn-primary erp-btn-sm inline-flex items-center gap-1.5"
                onClick={openCreateModal}
              >
                <PlusIcon className="h-4 w-4" aria-hidden />
                Nova chave
              </button>
            ) : null}
          </div>
        </div>

        {!canWrite ? (
          <p className="mb-4 text-xs text-slate-500">
            Criar, editar, revogar e revelar exigem permissão de escrita em Armazenamento.
          </p>
        ) : null}

        {keys.length === 0 ? (
          <p className="text-sm text-slate-500">
            Nenhuma chave adicional cadastrada. Use &quot;Nova chave&quot; para criar uma.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-2 py-2 font-medium">Nome</th>
                  <th className="px-2 py-2 font-medium">Access Key</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 font-medium">Criada / expira</th>
                  <th className="px-2 py-2 font-medium text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((item) => {
                  const revealed = revealedById[item.id];
                  return (
                    <tr key={item.id} className="border-b border-slate-100 align-top">
                      <td className="px-2 py-2">
                        <div className="font-medium text-slate-800">{item.name}</div>
                        {item.description ? (
                          <div className="mt-0.5 text-xs text-slate-500">{item.description}</div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 font-mono text-slate-700">
                        {revealed ? (
                          <div className="space-y-1">
                            <div>{revealed.accessKey}</div>
                            <div className="text-xs text-slate-500">{revealed.secretKey}</div>
                          </div>
                        ) : (
                          item.accessKeyMasked
                        )}
                      </td>
                      <td className="px-2 py-2">
                        {item.isActive ? (
                          <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                            Ativa
                          </span>
                        ) : item.revokedAtUtc ? (
                          <span className="inline-flex rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-800">
                            Revogada
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                            Expirada
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-slate-600">
                        <div>
                          {item.createdAtUtc
                            ? new Date(item.createdAtUtc).toLocaleString('pt-BR')
                            : '—'}
                        </div>
                        {item.expiresAtUtc ? (
                          <div className="mt-0.5 text-xs text-slate-500">
                            Expira {new Date(item.expiresAtUtc).toLocaleDateString('pt-BR')}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <div className="inline-flex flex-wrap justify-end gap-1">
                          {canWrite && item.isActive ? (
                            <button
                              type="button"
                              className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1"
                              disabled={revealingId === item.id}
                              onClick={() => void handleReveal(item)}
                            >
                              {revealed ? (
                                <EyeSlashIcon className="h-3.5 w-3.5" aria-hidden />
                              ) : (
                                <EyeIcon className="h-3.5 w-3.5" aria-hidden />
                              )}
                              {revealingId === item.id
                                ? '...'
                                : revealed
                                  ? 'Ocultar'
                                  : 'Revelar'}
                            </button>
                          ) : null}
                          {canWrite && item.isActive ? (
                            <button
                              type="button"
                              className="erp-btn-secondary erp-btn-sm"
                              onClick={() => openEditModal(item)}
                            >
                              Editar
                            </button>
                          ) : null}
                          {canWrite && item.isActive ? (
                            <button
                              type="button"
                              className="erp-btn-secondary erp-btn-sm"
                              onClick={() => void handleRevoke(item)}
                            >
                              Revogar
                            </button>
                          ) : null}
                          {canWrite ? (
                            <button
                              type="button"
                              className="erp-btn-danger erp-btn-sm"
                              onClick={() => void handleDelete(item)}
                            >
                              Excluir
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Conexão em uso</h3>
            <p className="mt-1 text-sm text-slate-500">
              Credenciais da conexão de armazenamento configurada na Visão geral
              {overview?.isCompanyConfigured ? ' (empresa)' : ' (padrão do host)'}.
            </p>
          </div>
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1.5"
            disabled={revealingConnection || !overview?.hasAccessKey || !canWrite}
            onClick={() => void handleToggleConnectionCredentials()}
          >
            {connectionVisible ? (
              <EyeSlashIcon className="h-4 w-4" aria-hidden />
            ) : (
              <EyeIcon className="h-4 w-4" aria-hidden />
            )}
            {revealingConnection
              ? 'Carregando...'
              : connectionVisible
                ? 'Ocultar'
                : 'Mostrar credenciais'}
          </button>
        </div>

        {overview?.isConfigured ? (
          <dl className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Access Key
              </dt>
              <dd className="mt-1 break-all font-mono text-sm text-slate-800">
                {connectionVisible && connectionAccessKey != null
                  ? connectionAccessKey
                  : overview.accessKeyMasked}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Secret Key
              </dt>
              <dd className="mt-1 break-all font-mono text-sm text-slate-800">
                {connectionVisible && connectionSecretKey != null
                  ? connectionSecretKey
                  : overview.secretKeyMasked}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-slate-500">
            Nenhuma conexão configurada. Salve endpoint e credenciais na Visão geral.
          </p>
        )}
      </div>

      <StorageAccessKeyFormModal
        open={modalOpen}
        initial={editing}
        saving={saving}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
